import { dirname } from 'node:path';
import React, { useState } from 'react';
import type { FC } from 'react';
import { Box, Text } from '@wix/cli-ui-kit';
import { isType } from 'variant';
import type { ComponentGenerationResult } from '@wix/gena';
import { useGenerationTask } from '../hooks/use-generation-task.js';
import { useProjectModel } from '../../../model.js';
import { generateAppTools } from './generate.js';
import { ChooseNameStep } from './steps.js';
import {
  CompleteGeneration,
  CompleteGenerationMessage,
} from '../CompleteGeneration.js';

const AppToolsSuccessMessage: FC<{
  generatedFiles: ComponentGenerationResult['newFiles'];
}> = ({ generatedFiles }) => {
  const { model } = useProjectModel();
  const folders = generatedFiles.reduce<Record<string, string[]>>(
    (acc, file) => {
      const dir = dirname(file);
      acc[dir] = [...(acc[dir] ?? []), file];
      return acc;
    },
    {}
  );

  return (
    <Box
      borderColor="blue"
      borderStyle="round"
      flexDirection="column"
      paddingY={1}
      paddingX={3}
      gap={1}
      marginTop={1}
    >
      <Box marginTop={-2} marginLeft={0}>
        <Text skin="question" bold>
          Generated Successfully
        </Text>
      </Box>
      <Text>✅ The new app tools has been generated.</Text>
      {Object.entries(folders).map(([folder, files]) => (
        <Box key={folder} flexDirection="column">
          <Text>📁 {folder}</Text>
          <Box flexDirection="column" marginLeft={3} marginTop={1}>
            {files.map((file) => (
              <Text key={file}>{file.replace(model.projectFolder, '.')}</Text>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const useGenerateAppTools = () => {
  const { model } = useProjectModel();
  const [name, setName] = useState<string>();

  const generationTask = useGenerationTask(async () => {
    if (name) {
      return generateAppTools(model.projectFolder, name);
    }
  }, [model.projectFolder, name]);

  const comp = isType(generationTask, 'Success') ? (
    <AppToolsSuccessMessage generatedFiles={generationTask.newFiles} />
  ) : null;

  return [
    <ChooseNameStep onSubmit={setName} name={name} />,
    <CompleteGeneration
      generationTask={generationTask}
      extensionType={'app tools'}
      successMessage={CompleteGenerationMessage.CustomSuccessMessage({ comp })}
      route={name}
    />,
  ];
};
